import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "../../validator";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createClientApp, getTemplatesModelsList } from "@/http";
import useSonner from "@/hooks/use-sonner";
import { LoaderCircle } from "lucide-react";
import { CreateApp, ModalTaskTypeENUM } from "@/types";
import useCurrentUserStore from "@/store";

import slugify from "react-slugify";

type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: (e?: number) => void;
};

const Step4Content: React.FC<PropTypes> = (props) => {
  const {
    setAppId,
    setAppSlug,
    setAppName,
    setUserData,
    selectedApp,
    setAppLoginToken,
  } = useCurrentUserStore();
  const { addToast } = useSonner();
  const step1Data = props.form.watch("step1");
  const step2Data = props.form.watch("step2");

  const createApp = useMutation<
    Awaited<ReturnType<typeof createClientApp>>, // Response type
    unknown, // Error type
    CreateApp // Variables type
  >({
    mutationFn: createClientApp,
    onSuccess: (res) => {
      addToast({
        message: res.data.data.message || `App created successfully.`,
        type: "success",
      });
      setAppSlug(res.data.data.result.slug);
      setAppId(res.data.data.result.id);
      setAppName(res.data.data.result.name);
      setUserData(res.data.data.result.userData);
      setAppLoginToken(res.data.data.result.userData.access_token_user);
      props.form.clearErrors();
      props.nextTrigger();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      step2Data.ai_models.length = 0;
      //  setIsSignInWithGoogle(false);
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong !",
        type: "error",
      });
    },
  });

  const handleTTT = (id: string) => {
    props.form.setValue("step2.text_to_text_modal", id);
  };
  const handleTTI = (id: string) => {
    props.form.setValue("step2.text_to_image_modal", id);
  };
  useQuery<Awaited<ReturnType<typeof getTemplatesModelsList>>>({
    queryKey: ["super-templates-models-list", 100, 1],
    queryFn: () =>
      getTemplatesModelsList(step1Data.appTemplateId).then((res) => {
        const TTTModals =
          res?.data?.data?.result?.filter((item) =>
            item?.type?.includes(ModalTaskTypeENUM.TTT)
          ) || [];

        const TTIModals =
          res?.data?.data?.result?.filter((item) =>
            item?.type?.includes(ModalTaskTypeENUM.TTI)
          ) || [];

        if (
          step1Data.requiredTemplateModalTypes?.includes(
            ModalTaskTypeENUM.TTT
          ) &&
          TTTModals?.length
        ) {
          const findModal = TTTModals.find((it) => it.name === "gpt-4o-mini");
          if (findModal) {
            handleTTT(findModal.id); // Ensure this does not trigger a state change causing a re-render loop
          }
        }

        // Handle TTI modals
        if (
          step1Data.requiredTemplateModalTypes?.includes(
            ModalTaskTypeENUM.TTI
          ) &&
          TTIModals?.length
        ) {
          const findModal = TTIModals.find(
            (it) => it.name === "dall-e-3" || it.name === "dall-e-2"
          );
          if (findModal) {
            handleTTI(findModal.id); // Ensure this does not trigger a state change causing a re-render loop
          }
        }
        return res;
      }),
    retry: 4,
  });

  const handleNextClick = () => {
    if (step2Data?.text_to_text_modal)
      step2Data.ai_models.push(step2Data.text_to_text_modal);
    if (step2Data?.text_to_image_modal)
      step2Data.ai_models.push(step2Data.text_to_image_modal);
    const resolve = clientOnboardingSchema.shape.step2.safeParse(step2Data);
    if (!resolve.success) {
      step2Data.ai_models.length = 0;
      addToast({
        message: resolve.error.errors[0].message || "Something went wrong !",
        type: "error",
      });
    }

    if (resolve.success) {
      createApp.mutate({
        name: step2Data.appName,
        description: step2Data.description || " ",
        ai_models: step2Data.ai_models,
        super_admin_app_type_id: step1Data.appTemplateId,
        is_paid: step2Data.is_paid,
        url: step2Data.appUrl,
        slug: slugify(step2Data.slug),
        localization: {},
        theme: {},
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="font-semibold text-[28px]/[32px]">
          Set Up Your Blog App
        </h2>
        <p className="text-sm text-muted-foreground">
          Give your app a name, set a URL, and add a description to get started.
        </p>
      </div>

      {/* App name */}
      <FormField
        control={props.form.control}
        name="step2.appName"
        defaultValue={step2Data.appName}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Blog Name</FormLabel>
            <FormControl>
              <Input
                {...field}
                className={cn("w-full", {
                  "border-destructive focus-visible:ring-transparent":
                    fieldState.error,
                })}
                onChange={(e) => {
                  field.onChange(e);
                  props.form.setValue(
                    "step2.appUrl",
                    `https://${slugify(e.target.value)}`
                  );
                  props.form.setValue("step2.slug", slugify(e.target.value));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Blog App URL */}
      <FormField
        control={props.form.control}
        name="step2.appUrl"
        defaultValue={step2Data.appUrl}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Blog URL</FormLabel>
            <div
              className={cn(
                "flex h-10 rounded-md border border-input bg-background text-sm",
                {
                  "border-destructive focus-visible:ring-transparent":
                    fieldState.error,
                }
              )}
            >
              <div className="w-full grid grid-cols-[65%_35%]">
                <FormControl>
                  <input
                    disabled
                    className="border-0 px-3 py-2 bg-background disabled:opacity-50 "
                    value={
                      field.value.includes("https://")
                        ? field.value
                        : "https://" + (field.value || "")
                    }
                  />
                </FormControl>
                <span className="border-l rounded-md rounded-l-none bg-muted flex items-center">
                  <span
                    className="px-3 py-2 text-xs truncate ..."
                    title={selectedApp?.url}
                  >
                    {selectedApp?.url}
                  </span>
                </span>
              </div>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Description */}
      <FormField
        control={props.form.control}
        name="step2.description"
        defaultValue={step2Data.description}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="What’s your blog about? (Optional)"
                className={cn("w-full", {
                  "border-destructive focus-visible:ring-transparent":
                    fieldState.error,
                })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* {step1Data.requiredTemplateModalTypes?.includes(ModalTaskTypeENUM.TTT) &&
      TTTModals?.length ? (
        <FormLabel>Select Models</FormLabel>
      ) : null}

      {step1Data.requiredTemplateModalTypes?.includes(ModalTaskTypeENUM.TTT) &&
      TTTModals?.length ? (
        <FormField
          control={props.form.control}
          name="step2.text_to_text_modal"
          defaultValue={(TTTModals?.length && TTTModals[0]?.id) || ""}
          render={({ field }) => (
            <FormItem>
              <p className="text-xs">Select Text Generation Modal</p>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={(TTTModals?.length && TTTModals[0]?.id) || ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select TTT Modal" />
                  </SelectTrigger>
                  <SelectContent>
                    {TTTModals?.length &&
                      TTTModals?.map((ttt) => {
                        if (!ttt) return null;
                        return (
                          <SelectItem key={ttt?.id} value={ttt?.id || ""}>
                            {ttt?.name}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      {step1Data.requiredTemplateModalTypes?.includes(ModalTaskTypeENUM.TTI) &&
      TTIModals?.length ? (
        <FormField
          control={props.form.control}
          name="step2.text_to_image_modal"
          defaultValue={(TTIModals?.length && TTIModals[0]?.id) || ""}
          render={({ field }) => (
            <FormItem>
              <p className="text-xs">Select Image Generation Modal</p>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={(TTIModals?.length && TTIModals[0]?.id) || ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select TTI Modal" />
                  </SelectTrigger>
                  <SelectContent>
                    {TTIModals?.length &&
                      TTIModals?.map((tti) => {
                        if (!tti) return null;
                        return (
                          <SelectItem key={tti.id} value={tti.id || " "}>
                            {tti?.name}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null} */}

      {/* next button */}
      <Button
        id="app-creation-step2"
        type="submit"
        disabled={createApp.isPending}
        className="w-full"
        onClick={handleNextClick}
      >
        {createApp.isPending && <LoaderCircle className="animate-spin" />}
        Next
      </Button>
    </div>
  );
};

export default Step4Content;
